export const withTimeout = (promise, ms) =>
    Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
    ]);

// ERP presigned S3 upload, called straight from the browser: there is no site
// backend on the production host (SuperHosting serves the build as static files).
// The Basic auth credentials are inlined into the bundle at build time.
const ERP_API_BASE = (process.env.REACT_APP_ERP_API_BASE || 'https://synergon-api.fidweb.net').replace(/\/$/, '');
const ERP_S3_USER = process.env.REACT_APP_ERP_S3_USER;
const ERP_S3_PASSWORD = process.env.REACT_APP_ERP_S3_PASSWORD;
const MAKE_PUBLIC = process.env.REACT_APP_S3_MAKE_PUBLIC !== 'false';

// btoa() only accepts latin1, so encode as UTF-8 bytes first.
const basicAuth = (user, password) => {
    const bytes = new TextEncoder().encode(`${user}:${password}`);
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return `Basic ${btoa(binary)}`;
};

// Pre-sanitized key: the ERP strips everything but letters, digits, . _ - per path segment.
const buildObjectKey = (fileName = '') => {
    const ext = String(fileName.split('.').pop() || '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .slice(0, 8)
        .toLowerCase();
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const id = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}`;
    return ext ? `uploads/${year}/${month}/${id}.${ext}` : `uploads/${year}/${month}/${id}`;
};

const requestPresignedUpload = async (file) => {
    if (!ERP_S3_USER || !ERP_S3_PASSWORD) throw new Error('s3-credentials-missing');

    const mimeType = file?.type || 'application/octet-stream';
    const response = await fetch(`${ERP_API_BASE}/cloudServices/website/s3Upload`, {
        method: 'POST',
        headers: {
            Authorization: basicAuth(ERP_S3_USER, ERP_S3_PASSWORD),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            objectKey: buildObjectKey(file?.name || ''),
            mimeType,
            makePublic: MAKE_PUBLIC,
        }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.presignedUrl || !data?.url) {
        throw new Error(`presign-failed-${response.status}`);
    }
    return { ...data, mimeType };
};

const putToS3 = async (file, { presignedUrl, mimeType }) => {
    // Raw PUT, no x-amz-* headers: the ERP bakes the ACL into the signed URL as a
    // query param and signs only `host`, so any extra x-amz-* header sent here is
    // unsigned and S3 rejects the whole request with 403 AccessDenied.
    const headers = { 'Content-Type': mimeType || file?.type || 'application/octet-stream' };

    const response = await fetch(presignedUrl, { method: 'PUT', headers, body: file });
    if (!response.ok) throw new Error(`s3-put-${response.status}`);
};

export const uploadFiles = (files) =>
    Promise.all(
        Array.from(files || []).map(async (file) => {
            const presigned = await requestPresignedUpload(file);
            await putToS3(file, presigned);
            return { location: presigned.url };
        })
    );

export const uploadFilesWithTimeout = (files, ms = 25000) => withTimeout(uploadFiles(files), ms);

// Simple slugify for titles (keeps non-Latin characters URL-encoded by the browser)
export const slugify = (value = '') =>
    value
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // strip accents for Latin
        .replace(/[^a-z0-9\u0400-\u04FF\s-]/g, '') // keep Cyrillic letters
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');

// Language-aware path helpers
export const hasEnPrefix = (pathname = '') => pathname === '/en' || pathname.startsWith('/en/');
export const stripEnPrefix = (pathname = '') => (hasEnPrefix(pathname) ? pathname.replace(/^\/en(\/|$)/, '/') : pathname || '/');
export const langPrefix = (lang = 'bg') => (lang === 'en' ? '/en' : '');
export const toLangPath = (path = '/', lang = 'bg') => {
    const clean = path === '/' ? '/' : path.startsWith('/') ? path : `/${path}`;
    if (lang === 'en') {
        if (hasEnPrefix(clean)) return clean; // already prefixed
        return clean === '/' ? '/en' : `/en${clean}`;
    }
    // bg: ensure no /en prefix
    return stripEnPrefix(clean);
};

export const getNavTab = (pathname = '') => {
    const path = stripEnPrefix(pathname);
    if (path === '/' || path === '') return 'home';
    if (path.startsWith('/about')) return 'about';
    if (
        path.startsWith('/products-and-services') ||
        path.startsWith('/users') ||
        path.startsWith('/manufacturers') ||
        path.startsWith('/additional-services')
    ) return 'products-and-services';
    if (path.startsWith('/partners')) return 'partners';
    if (path.startsWith('/info') || path.startsWith('/article')) return 'info';
    if (path.startsWith('/resources')) return 'resources';
    if (path.startsWith('/contact')) return 'contact';
    return '';
};